import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, MessageCircle, ShieldCheck, GraduationCap, Compass, Quote } from "lucide-react";
import { Button } from "../components/ui/button";

const stages = [
  "Coursework", "Comps", "Proposal", "IRB", "Research",
  "Analysis", "Dissertation", "Defense", "Graduation", "Publishing",
];

const features = [
  {
    icon: Users,
    title: "Circles, not feeds",
    body: "Private communities keyed to your stage, methodology, and program — not another endless timeline.",
  },
  {
    icon: MessageCircle,
    title: "Group + direct messages",
    body: "Group threads for shared struggles, direct messages for the honest 2 a.m. conversation.",
  },
  {
    icon: ShieldCheck,
    title: "Trust & Safety by design",
    body: "Confidential reporting, moderator review, and appeal workflow — with strict reporter privacy and separation of evidence from ordinary discussion.",
  },
  {
    icon: GraduationCap,
    title: "Mentors who've been there",
    body: "Graduates, dissertation coaches, and doctors who can share candid guidance from their own doctoral experience.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/40 backdrop-blur-xl bg-background/70">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="font-serif text-xl font-black tracking-tight">DocCircle</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#journey" className="text-muted-foreground hover:text-foreground transition-colors">Journey</a>
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#for-who" className="text-muted-foreground hover:text-foreground transition-colors">Who it's for</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="header-login-btn">Sign in</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" data-testid="header-register-btn" className="rounded-full">
                Join a circle
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-primary mb-6" data-testid="hero-eyebrow">
              A doctoral success ecosystem
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
              The doctoral journey <em className="text-primary not-italic">isn't meant</em> to be walked alone.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              DocCircle is a private community for PhD, EdD, DBA, DNP, PsyD, and DrPH candidates. Real
              conversations, methodology help, IRB veterans, and dissertation coaches — all in one calm,
              scholarly space.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-primary" className="rounded-full px-8">
                  Start your circle <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-cta-secondary" className="rounded-full px-8">
                  I already have an account
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <span>· PhD · EdD · DBA</span>
              <span>· DNP · PsyD · DrPH</span>
              <span>· Master's thesis candidates</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border">
              <img
                src="https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxkb2N0b3JhbCUyMHN0dWRlbnQlMjBzdHVkeWluZ3xlbnwwfHx8fDE3ODYyOTMwMzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Doctoral candidate studying"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <Quote className="h-6 w-6 text-primary mb-3" />
                <p className="font-serif text-lg leading-snug">
                  "I found a proposal-writing circle at 2 a.m. and finally slept."
                </p>
                <p className="text-xs text-muted-foreground mt-2">— EdD candidate, Year 3</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey timeline */}
      <section id="journey" className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-7">
              <h2 className="font-serif text-4xl lg:text-5xl font-black tracking-tight">
                Every stage. Every question. Every wall.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-muted-foreground leading-relaxed">
                We built DocCircle around the actual doctoral arc — not generic social feeds. Wherever you are,
                someone here is one step ahead and willing to talk.
              </p>
            </div>
          </div>
          <ol className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="journey-timeline">
            {stages.map((s, i) => (
              <li
                key={s}
                className="relative bg-card border border-border rounded-lg p-4 hover:-translate-y-1 transition-transform"
                style={{ transitionProperty: "transform, box-shadow" }}
              >
                <span className="text-xs text-muted-foreground">Stage {String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-lg font-medium mt-1">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-primary mb-4">What's inside</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-black tracking-tight">
            Built for doctoral candidates, not the metrics feed.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {features.map((f) => (
            <div
              key={f.title}
              className="border border-border rounded-xl p-8 lg:p-10 bg-card hover:-translate-y-1 transition-transform"
              style={{ transitionProperty: "transform, box-shadow" }}
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <f.icon className="h-8 w-8 text-primary mb-6" />
              <h3 className="font-serif text-2xl font-black mb-3">{f.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section id="for-who" className="border-t border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-border">
              <img
                src="https://images.unsplash.com/photo-1579389082289-3d6922d506c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxkb2N0b3JhbCUyMHN0dWRlbnQlMjBzdHVkeWluZ3xlbnwwfHx8fDE3ODYyOTMwMzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Mentor at desk"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <Compass className="h-8 w-8 text-primary mb-6" />
            <h2 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mb-6">
              For candidates who want scholarship <em className="not-italic text-primary">and</em> sanity.
            </h2>
            <ul className="space-y-4 text-muted-foreground leading-relaxed">
              <li>· <span className="text-foreground font-medium">Current candidates</span> — matched with peers at your exact stage.</li>
              <li>· <span className="text-foreground font-medium">Graduates</span> — pay forward the mentorship you wish you'd had.</li>
              <li>· <span className="text-foreground font-medium">Coaches & consultants</span> — build a private client circle.</li>
              <li>· <span className="text-foreground font-medium">Faculty & Doctors</span> — a peer-scholarship space alongside (not replacing) your institution's formal supervisory workflow.</li>
              <li>· <span className="text-foreground font-medium">Universities & graduate schools</span> — coming soon: cohort licensing.</li>
            </ul>
            <div className="mt-10">
              <Link to="/register">
                <Button size="lg" data-testid="foot-cta" className="rounded-full px-8">
                  Create your account <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            <span className="font-serif">DocCircle</span>
          </div>
          <p>© {new Date().getFullYear()} DocCircle. Built for the long, quiet marathon.</p>
        </div>
      </footer>
    </div>
  );
}
